import {PageHeader} from "@ant-design/pro-layout";
import {navigateBackHandler} from "../../helpers/mainHelpers";
import React from "react";
import {SchedulePeriod} from "./types";
import {Button, DatePicker, Empty, Segmented, Spin} from "antd";
import useSurgerySchedulePageService from "./useSurgerySchedulePageService";
import dayjs from "dayjs";
import OperationCard from "./components/operation-card/OperationCard";
import useOperationStaffController from "../../controllers/operationStaff-controller";
import SurgeryRequestDetailsModal from "../../components/modals/surgeryRequestDetails-modal/SurgeryRequestDetailsModal";
import {CalendarOutlined, MedicineBoxOutlined} from "@ant-design/icons";
import ScrollToTopButton from "../../components/main-buttons/scroll-button/ScrollToTopButton";
import "./style.less";

const SurgerySchedulePage = () => {
  const {
    period,
    setPeriod,
    selectedDate,
    setSelectedDate,
    groupedByRoom,
    operationRoomsList,
    detailsButtonHandler,
    isDetailsModalOpen,
    surgeryRequestsController,
    isLoading
  } = useSurgerySchedulePageService();
  const {operationStaffList} = useOperationStaffController();
  const isCurrentPeriod = selectedDate.isSame(dayjs(), period);
  const currentPeriodLabel = period === "day" ? "Сьогодні" : period === "week" ? "Поточний тиждень" : "Поточний місяць";

  const selectedPeriodLabel = period === "day"
    ? selectedDate.format("DD.MM.YYYY")
    : period === "week"
      ? `${selectedDate.startOf("week").format("DD.MM")} — ${selectedDate.endOf("week").format("DD.MM.YYYY")}`
      : selectedDate.format("MM.YYYY");

  return <>
    <div className="page_title">
      <PageHeader
        title={<div className="page-header_title">
          <div className="page_name">Розклад операцій</div>
        </div>}
        onBack={navigateBackHandler}
      />
    </div>

    <div className="page_content surgery-schedule-page">
      <section className="surgery-schedule-section">
        <div className="surgery-schedule-toolbar">
          <div className="surgery-schedule-toolbar__controls">
            <Segmented
              className="surgery-schedule-toolbar__period"
              value={period}
              onChange={(value) => setPeriod(value as SchedulePeriod)}
              options={[
                {label: "День", value: "day"},
                {label: "Тиждень", value: "week"},
                {label: "Місяць", value: "month"},
              ]}
            />
            <DatePicker
              className="surgery-schedule-toolbar__picker"
              format={period === "week" ? (value) => `${value.startOf("week").format("DD.MM")} — ${value.endOf("week").format("DD.MM")}` : undefined}
              picker={period === "month" ? "month" : period === "week" ? "week" : "date"}
              value={selectedDate}
              allowClear={false}
              onChange={(value) => value && setSelectedDate(value)}
            />
            <Button
              className={`surgery-schedule-toolbar__today${period === "day" && isCurrentPeriod ? " surgery-schedule-toolbar__today--active" : ""}`}
              aria-pressed={period === "day" && isCurrentPeriod}
              onClick={() => {
                setPeriod("day");
                setSelectedDate(dayjs());
              }}>Сьогодні</Button>
          </div>
          <div className="surgery-schedule-toolbar__selection">
            <CalendarOutlined/>
            <span><small>{isCurrentPeriod ? currentPeriodLabel : "Обраний період"}</small><strong>{selectedPeriodLabel}</strong></span>
          </div>
        </div>

        <div className="surgery-schedule-cards" role="region" aria-label="Картки операцій" tabIndex={0}>
        <Spin spinning={isLoading}>
          {operationRoomsList.length ? <div className="surgery-rooms-grid">
            {operationRoomsList.map((room) => {
              const roomItems = groupedByRoom[room.$id] ?? [];

              return <section className="surgery-room" key={room.$id}>
                <header className="surgery-room__header">
                  <span className="surgery-room__icon"><MedicineBoxOutlined/></span>
                  <span className="surgery-room__heading">
                    <small>Операційна</small>
                    <strong>{room.name}</strong>
                  </span>
                  <span className="surgery-room__count">{roomItems.length}</span>
                </header>

                <div className="surgery-room__body">
                  {roomItems.length ? roomItems
                    .sort((first, second) => dayjs(first.operationTime).valueOf() - dayjs(second.operationTime).valueOf())
                    .map((item) => <OperationCard
                      key={item.$id || `${item.operationTime}-${item.patientName}`}
                      operationStaffList={operationStaffList}
                      item={item}
                      cardClickHandler={detailsButtonHandler}
                    />) : <div className="surgery-room__empty">
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Операцій немає"/>
                  </div>}
                </div>
              </section>;
            })}
          </div> : <div className="surgery-schedule-empty">
            <Empty description="Операційних залів поки немає"/>
          </div>}
        </Spin>
        </div>
      </section>
      <ScrollToTopButton elementSelector={".surgery-schedule-cards"}/>
    </div>

    {isDetailsModalOpen && <SurgeryRequestDetailsModal controller={surgeryRequestsController}/>}
  </>;
};

export default SurgerySchedulePage;
